<script lang="ts" setup generic="T">
import { computed, ref, watch } from 'vue';
import ElIconButton from '@/ElIconButton.vue';

interface Props {
  rows: T[];
  rowsPerPage?: number;
  pagesBeforeDots?: number; // < 1,2,3....13,14,15 > in this case it's 3
  hideFooter?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  rowsPerPage: 15,
  pagesBeforeDots: 3,
  hideFooter: false,
});

const currentPage = ref(0);

const totalRowsNumber = computed(() => props.rows.length);

const emitCurrentTabRows = () => emit('update:currentTabRows', props.rows.slice(currentStartItem.value, currentEndItem.value + 1));

const currentStartItem = computed(() => {
  return currentPage.value * props.rowsPerPage;
});

const currentEndItem = computed(() => {
  const end = currentStartItem.value + props.rowsPerPage - 1;
  return end <= totalRowsNumber.value ? end : totalRowsNumber.value - 1;
});

const emit = defineEmits<{
  (event: 'update:currentTabRows', rows: T[]): void;
  (event: 'update:currentPage', page: number): void;
  (event: 'change', page: number): void;
}>();

// number of pages, given number of rows and rows per page
const totalPages = computed(() => Math.ceil(totalRowsNumber.value / props.rowsPerPage));

const prevPageDisabled = computed(() => currentPage.value <= 0);
const nextPageDisabled = computed(() => currentPage.value >= totalPages.value - 1);

const onPageChange = (page: number) => {
  if (page > 0 && page <= totalPages.value && page !== currentPage.value) {
    emit('update:currentPage', page);
    emit('change', page);
  }
  emitCurrentTabRows();
};

// reset current page if filters have been applied
watch(
  () => props.rows,
  () => {
    currentPage.value = 0;
  },
);

const goToPage = (pageNumber: number) => {
  console.log('goToPage', pageNumber, currentPage.value, sliceStart.value, totalPages.value);
  // only allow going to valid pages
  if (pageNumber < 0 || pageNumber >= totalPages.value) return;
  currentPage.value = pageNumber;
  onPageChange(pageNumber);
};

function useNumberedPages() {
  const sliceStart = ref(0);
  const sliceArray = computed(() => range(totalPages.value).slice(sliceStart.value, sliceStart.value + props.pagesBeforeDots));

  watch(
    () => [props.pagesBeforeDots, props.rows, props.rowsPerPage, totalPages.value, currentPage.value],
    () => {
      calculateSliceStart();
      emitCurrentTabRows();
    },
    {
      immediate: true,
    },
  );

  const range = (end: number, start: number = 0, step: number = 1) => {
    const arr = [];
    for (let i = start; i < end; i += step) {
      arr.push(i);
    }
    return arr;
  };

  function calculateSliceStart() {
    const chunkSize = props.pagesBeforeDots;
    const currentChunkStart = sliceStart.value;
    const currentChunkEnd = currentChunkStart + chunkSize;

    if (currentPage.value >= currentChunkEnd) {
      const lastChunkStart = totalPages.value - chunkSize;
      if (currentPage.value > lastChunkStart) {
        sliceStart.value = lastChunkStart;
      } else {
        sliceStart.value = currentPage.value;
      }
    } else if (currentPage.value < currentChunkStart) {
      if (currentPage.value > chunkSize) {
        sliceStart.value = currentPage.value + 1 - chunkSize;
      } else {
        if (currentPage.value >= 3) {
          sliceStart.value = currentPage.value - 1;
        } else {
          sliceStart.value = 0;
        }
      }
    }
  }

  return {
    sliceStart,
    sliceArray,
  };
}
const { sliceStart, sliceArray } = useNumberedPages();
</script>

<template>
  <div v-if="!hideFooter" class="bg-white flex items-center rounded-b-md border-t border-neutral-surface px-4 sm:px-6 py-4">
    <!-- Small Screen -->
    <ul class="pagination w-fit mx-auto flex items-center rounded-md border border-surface bg-white md:hidden">
      <!--Previous-->
      <li class="min-w-8">
        <ElIconButton
          class="-ml-px"
          :icon="{
            name: 'ChevronLeftIcon',
          }"
          :disabled="prevPageDisabled"
          @click="goToPage(currentPage - 1)"
        />
      </li>

      <!--Pages-->
      <li
        v-for="item in sliceArray"
        :key="item"
        :class="['border-x-[1px] border-surface -ml-px', currentPage === item && 'border-primary -my-px']"
      >
        <button
          type="button"
          :class="['text-sm min-w-8 h-8', currentPage === item ? 'font-semibold bg-primary text-white' : 'text-neutral-600']"
          @click.prevent="goToPage(item)"
        >
          {{ item + 1 }}
        </button>
      </li>

      <!-- Expand pages button (...) -->
      <li
        v-if="sliceStart < totalPages - pagesBeforeDots && totalPages > 3 && (sliceArray.at(-1) ?? 0) < totalPages - 1"
        class="border-x-[1px] border-surface -my-px -ml-px"
      >
        <button type="button" class="text-neutral-600 text-sm min-w-8 h-8" @click.prevent="goToPage((sliceArray.at(-1) ?? 0) + 1)">
          <span aria-hidden="true">&hellip;</span>
        </button>
      </li>

      <!-- Next -->
      <li class="min-w-8">
        <ElIconButton
          :icon="{
            name: 'ChevronRightIcon',
          }"
          :disabled="nextPageDisabled"
          @click="goToPage(currentPage + 1)"
        />
      </li>
    </ul>

    <!-- Large Screen -->
    <div class="hidden md:flex flex-1 items-center w-full">
      <nav class="mx-auto" aria-label="Page navigation">
        <ul class="pagination flex items-center rounded-md border border-surface bg-white">
          <!--Previous-->
          <li>
            <ElIconButton
              :icon="{
                name: 'ChevronLeftIcon',
              }"
              :disabled="prevPageDisabled"
              @click="goToPage(currentPage - 1)"
            />
          </li>

          <!-- First 2 pages -->
          <li v-if="sliceStart > 0 && totalPages > pagesBeforeDots" class="border-x-[1px] border-surface -ml-px">
            <button type="button" class="text-sm min-w-8 h-8 text-neutral-600" @click.prevent="goToPage(0)">1</button>
          </li>
          <li
            v-if="(sliceStart > 2 && totalPages > pagesBeforeDots + 1) || (sliceStart > 0 && totalPages === 5)"
            class="border-x-[1px] border-surface -ml-px"
          >
            <button type="button" class="text-sm min-w-8 h-8 text-neutral-600" @click.prevent="goToPage(1)">2</button>
          </li>

          <!-- Expand pages button (...) -->
          <li v-if="sliceStart > 0 && totalPages > 5" class="border-x-[1px] border-surface -my-px -ml-px">
            <button type="button" class="text-neutral-600 text-sm min-w-8 h-8" @click.prevent="goToPage((sliceArray.at(0) ?? 1) - 1)">
              <span aria-hidden="true">&hellip;</span>
            </button>
          </li>

          <!--Pages-->
          <li
            v-for="item in sliceArray"
            :key="item"
            :class="['border-x-[1px] border-surface -ml-px', currentPage === item && 'border-primary -my-px']"
          >
            <button
              type="button"
              :class="['text-sm min-w-8 h-8', currentPage === item ? 'font-semibold bg-primary text-white' : 'text-neutral-600']"
              @click.prevent="goToPage(item)"
            >
              {{ item + 1 }}
            </button>
          </li>

          <!-- Expand pages button (...) -->
          <li
            v-if="sliceStart < totalPages - pagesBeforeDots && totalPages > 5 && (sliceArray.at(-1) ?? 0) < totalPages - 2"
            class="border-x-[1px] border-surface -my-px -ml-px"
          >
            <button type="button" class="text-neutral-600 text-sm min-w-8 h-8" @click.prevent="goToPage((sliceArray.at(-1) ?? 0) + 1)">
              <span aria-hidden="true">&hellip;</span>
            </button>
          </li>

          <!-- Last 2 pages -->
          <li
            v-if="
              sliceStart < totalPages - pagesBeforeDots &&
              totalPages >= pagesBeforeDots + 2 &&
              (totalPages === 5 || (sliceArray.at(-1) ?? 0) < totalPages - 3)
            "
            class="border-x-[1px] border-surface -ml-px"
          >
            <button type="button" class="text-sm min-w-8 h-8 text-neutral-600" @click.prevent="goToPage(totalPages - 2)">
              {{ totalPages - 1 }}
            </button>
          </li>
          <li
            v-if="sliceStart < totalPages - pagesBeforeDots && totalPages >= pagesBeforeDots + 1"
            class="border-x-[1px] border-surface -ml-px"
          >
            <button type="button" class="text-sm min-w-8 h-8 text-neutral-600" @click.prevent="goToPage(totalPages - 1)">
              {{ totalPages }}
            </button>
          </li>

          <!-- Next -->
          <li>
            <ElIconButton
              :icon="{
                name: 'ChevronRightIcon',
              }"
              :disabled="nextPageDisabled"
              @click="goToPage(currentPage + 1)"
            />
          </li>
        </ul>
      </nav>
    </div>
  </div>
</template>
